import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AppHeader } from './AppHeader.jsx';
import { ModuleNav } from './ModuleNav.jsx';

const PIN_KEY = 'sidebar.pinned';
const LEGACY_COLLAPSED_KEY = 'sidebar.collapsed';

function readInitialPinned() {
  try {
    const v = localStorage.getItem(PIN_KEY);
    if (v !== null) return v !== '0';
    // One-time migration from legacy collapsed key: collapsed=1 → unpinned.
    const legacy = localStorage.getItem(LEGACY_COLLAPSED_KEY);
    if (legacy !== null) {
      localStorage.removeItem(LEGACY_COLLAPSED_KEY);
      return legacy !== '1';
    }
  } catch {
    // ignore quota / private-mode errors
  }
  // Default: hover-only mode. Operators must explicitly pin to keep the
  // sidebar in flow. Maximizes content width on first visit.
  return false;
}

/**
 * Top-level layout: 64px top bar, sidebar (pinned w-64 in flow OR auto-mode
 * w-16 spacer + w-64 overlay on hover), centered content capped at 1920px.
 */
export function AppShell() {
  const [pinned, setPinned] = useState(readInitialPinned);

  useEffect(() => {
    try {
      localStorage.setItem(PIN_KEY, pinned ? '1' : '0');
    } catch {
      // ignore quota / private-mode errors
    }
  }, [pinned]);

  // Keyboard: "[" toggles pin (per UI_FRAMEWORK §7)
  useEffect(() => {
    function onKey(e) {
      if (e.key === '[' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const activeTag = document.activeElement?.tagName;
        if (activeTag !== 'INPUT' && activeTag !== 'TEXTAREA') {
          setPinned((p) => !p);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-sky-100">
      <AppHeader />
      <div className="flex flex-1">
        <ModuleNav pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
        <main className="flex-1 overflow-x-hidden">
          <div className="mx-auto max-w-[1920px] px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
